import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { PlaybookRecommendation, PlaybookSession, PlaybooksState, PlaybookStats } from '../../types';
import { PlaybooksService } from '../../services/playbooksService';

export const fetchPendingHITLTotal = createAsyncThunk(
  'playbooks/fetchPendingHITLTotal',
  async (clientIds: string[]) => {
    if (!clientIds.length) return 0;
    const results = await Promise.all(
      clientIds.map((id) =>
        PlaybooksService.getReviewQueue(id, { hitl_only: true, limit: 1 })
          .then((r) => r.pending_hitl)
          .catch(() => 0)
      )
    );
    return results.reduce((sum, n) => sum + n, 0);
  }
);

export const fetchPlaybookSessions = createAsyncThunk(
  'playbooks/fetchSessions',
  async (params: { client_id?: string; limit?: number; offset?: number } = {}) => {
    return await PlaybooksService.getSessions(params);
  }
);

export const fetchRecommendationsForAlert = createAsyncThunk(
  'playbooks/fetchForAlert',
  async (alertId: string) => {
    return await PlaybooksService.getRecommendationsForAlert(alertId);
  }
);

export const fetchRecommendationsForSession = createAsyncThunk(
  'playbooks/fetchForSession',
  async (sessionId: string) => {
    return await PlaybooksService.getRecommendationsForSession(sessionId);
  }
);

export const fetchPlaybookStats = createAsyncThunk(
  'playbooks/fetchStats',
  async (clientId: string) => {
    return await PlaybooksService.getPlaybookStats(clientId);
  }
);

export const submitRecommendationOutcome = createAsyncThunk(
  'playbooks/submitOutcome',
  async ({
    recommendationId,
    outcome,
    actionFeedback,
  }: {
    recommendationId: string;
    outcome: string;
    actionFeedback: { action: string; decision: string }[];
  }) => {
    return await PlaybooksService.updateRecommendation(recommendationId, outcome, actionFeedback);
  }
);

const initialState: PlaybooksState = {
  sessions: [],
  selectedSession: null,
  recommendations: [],
  stats: null,
  statsLoading: false,
  isLoading: false,
  isDetailsLoading: false,
  error: null,
  pendingHITLTotal: 0,
};

const playbooksSlice = createSlice({
  name: 'playbooks',
  initialState,
  reducers: {
    clearError: (state) => { state.error = null; },
    setSelectedSession: (state, action: PayloadAction<PlaybookSession | null>) => {
      state.selectedSession = action.payload;
    },
    clearRecommendations: (state) => {
      state.recommendations = [];
      state.selectedSession = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPlaybookSessions.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchPlaybookSessions.fulfilled, (state, action) => {
        state.isLoading = false;
        state.sessions = action.payload.sessions;
      })
      .addCase(fetchPlaybookSessions.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to fetch sessions';
      })
      .addCase(fetchRecommendationsForAlert.pending, (state) => {
        state.isDetailsLoading = true;
        state.error = null;
      })
      .addCase(fetchRecommendationsForAlert.fulfilled, (state, action) => {
        state.isDetailsLoading = false;
        state.selectedSession = action.payload.session;
        state.recommendations = action.payload.recommendations;
      })
      .addCase(fetchRecommendationsForAlert.rejected, (state, action) => {
        state.isDetailsLoading = false;
        state.error = action.error.message || 'Failed to fetch recommendations';
      })
      .addCase(fetchRecommendationsForSession.pending, (state) => {
        state.isDetailsLoading = true;
        state.error = null;
      })
      .addCase(fetchRecommendationsForSession.fulfilled, (state, action) => {
        state.isDetailsLoading = false;
        state.selectedSession = action.payload.session;
        state.recommendations = action.payload.recommendations;
      })
      .addCase(fetchRecommendationsForSession.rejected, (state, action) => {
        state.isDetailsLoading = false;
        state.error = action.error.message || 'Failed to fetch recommendations';
      })
      .addCase(fetchPlaybookStats.pending, (state) => {
        state.statsLoading = true;
      })
      .addCase(fetchPlaybookStats.fulfilled, (state, action: PayloadAction<PlaybookStats>) => {
        state.statsLoading = false;
        state.stats = action.payload;
      })
      .addCase(fetchPlaybookStats.rejected, (state) => {
        state.statsLoading = false;
      })
      .addCase(submitRecommendationOutcome.fulfilled, (state, action) => {
        const idx = state.recommendations.findIndex(r => r.id === action.payload.id);
        if (idx !== -1) {
          state.recommendations[idx] = action.payload;
        }
      })
      .addCase(fetchPendingHITLTotal.fulfilled, (state, action) => {
        state.pendingHITLTotal = action.payload;
      });
  },
});

export const { clearError, setSelectedSession, clearRecommendations } = playbooksSlice.actions;
export default playbooksSlice.reducer;